import { z } from 'zod'
import type { LineItemType } from './db/schema/receivables/receivables.schema'

/**
 * Aufmaß: internal measurement rows on a line item. Each row's formula evaluates to a number,
 * and the rounded sum of the rows *is* the line's quantity. Never printed or exported.
 *
 * Only formulas are stored; results are recomputed here, by the editor for display and by the
 * server on every write, so a stored quantity can never disagree with its Aufmaß.
 */
export interface Measurement {
  id: string
  description: string
  formula: string
}

export const MEASUREMENTS_MAX_ROWS = 100
export const MEASUREMENT_DESCRIPTION_MAX_LENGTH = 200
export const MEASUREMENT_FORMULA_MAX_LENGTH = 200
/** The quantity range the editor's quantity input accepts. */
export const MEASUREMENT_QUANTITY_MIN = 0.01
export const MEASUREMENT_QUANTITY_MAX = 999_999.99

export const measurementsSchema = z
  .array(
    z.object({
      id: z.string().min(1),
      description: z.string().max(MEASUREMENT_DESCRIPTION_MAX_LENGTH),
      formula: z.string().trim().min(1).max(MEASUREMENT_FORMULA_MAX_LENGTH),
    }),
  )
  .max(MEASUREMENTS_MAX_ROWS)
  .nullable()
  .optional()

const MEASURABLE_LINE_TYPES: ReadonlySet<LineItemType> = new Set(['product', 'service', 'editor'])

/** Lines without a quantity of their own (titles, bundles, references) carry no Aufmaß. */
export function lineItemTypeAllowsMeasurements(lineItemType: LineItemType | null | undefined) {
  // The column defaults to `editor`, so an absent type is an editor line.
  return MEASURABLE_LINE_TYPES.has(lineItemType ?? 'editor')
}

/**
 * Evaluates `+ - * /`, parentheses, unary signs and decimal numbers (`2,5` or `2.5`).
 * Returns `null` for anything else, including division by zero. A hand-written recursive-descent
 * parser: formulas are user input and must never reach `eval`.
 */
export function evaluateMeasurementFormula(formula: string): number | null {
  const tokens = formula.match(/\d+(?:[.,]\d+)?|[.,]\d+|\S/g)
  if (!tokens) {
    return null
  }
  let pos = 0

  // expr := term (('+' | '-') term)*
  function expr(): number | null {
    let left = term()
    while (left !== null && (tokens?.[pos] === '+' || tokens?.[pos] === '-')) {
      const op = tokens[pos++]
      const right = term()
      left = right === null ? null : op === '+' ? left + right : left - right
    }
    return left
  }

  // term := factor (('*' | '/') factor)*
  function term(): number | null {
    let left = factor()
    while (left !== null && (tokens?.[pos] === '*' || tokens?.[pos] === '/')) {
      const op = tokens[pos++]
      const right = factor()
      if (right === null || (op === '/' && right === 0)) {
        return null
      }
      left = op === '*' ? left * right : left / right
    }
    return left
  }

  // factor := ('+' | '-') factor | number | '(' expr ')'
  function factor(): number | null {
    const token = tokens?.[pos++]
    if (token === '+' || token === '-') {
      const value = factor()
      return value === null ? null : token === '-' ? -value : value
    }
    if (token === '(') {
      const value = expr()
      return tokens?.[pos++] === ')' ? value : null
    }
    if (token !== undefined && /^[.,]?\d/.test(token)) {
      return Number(token.replace(',', '.'))
    }
    return null
  }

  const result = expr()
  return pos === tokens.length && result !== null && Number.isFinite(result) ? result : null
}

function roundQuantity(value: number) {
  return Math.round((value + Number.EPSILON * Math.sign(value)) * 100) / 100
}

export type MeasurementsSum =
  | { ok: true; quantity: number }
  | { ok: false; reason: 'invalid_formula'; rowIndex: number }
  | { ok: false; reason: 'out_of_range'; quantity: number }

/** The quantity the rows add up to, rounded to the quantity's two decimals. */
export function sumMeasurements(rows: readonly Pick<Measurement, 'formula'>[]): MeasurementsSum {
  let sum = 0
  for (const [rowIndex, row] of rows.entries()) {
    const value = evaluateMeasurementFormula(row.formula)
    if (value === null) {
      return { ok: false, reason: 'invalid_formula', rowIndex }
    }
    sum += value
  }
  const quantity = roundQuantity(sum)
  if (quantity < MEASUREMENT_QUANTITY_MIN || quantity > MEASUREMENT_QUANTITY_MAX) {
    return { ok: false, reason: 'out_of_range', quantity }
  }
  return { ok: true, quantity }
}

export type ApplyMeasurementsResult<T> =
  | { ok: true; line: T }
  | { ok: false; reason: 'not_allowed' }
  | Exclude<MeasurementsSum, { ok: true }>

/**
 * The server-side rule for a line item written by a user: when it carries an Aufmaß, its
 * quantity is whatever the rows add up to, regardless of the quantity the client sent.
 */
export function applyMeasurements<
  T extends {
    lineItemType?: LineItemType | null
    quantity: number
    measurements?: Measurement[] | null
  },
>(line: T): ApplyMeasurementsResult<T> {
  if (line.measurements === undefined || line.measurements === null) {
    return { ok: true, line }
  }
  if (line.measurements.length === 0) {
    return { ok: true, line: { ...line, measurements: null } }
  }
  if (!lineItemTypeAllowsMeasurements(line.lineItemType)) {
    return { ok: false, reason: 'not_allowed' }
  }
  const sum = sumMeasurements(line.measurements)
  return sum.ok ? { ok: true, line: { ...line, quantity: sum.quantity } } : sum
}

const measurementsErrorMessages = {
  not_allowed: 'This line type cannot carry an Aufmaß',
  invalid_formula: 'An Aufmaß formula is invalid',
  out_of_range: `An Aufmaß must add up to between ${MEASUREMENT_QUANTITY_MIN} and ${MEASUREMENT_QUANTITY_MAX}`,
} as const

const BUNDLE_LINE_TYPES: ReadonlySet<LineItemType> = new Set([
  'receivable_bundle',
  'catalog_bundle',
])

/**
 * Zod transform for a document's line-item input: rejects a bad Aufmaß, or sets each line's
 * quantity from it. A bundle child's Aufmaß is dropped rather than applied: the bundle scales its
 * children's quantities, which an Aufmaß sum would silently undo.
 */
export function enforceMeasurements<
  T extends Parameters<typeof applyMeasurements>[0] & {
    id?: string | null
    parentId?: string | null
  },
>(lines: T[], ctx: z.core.$RefinementCtx<T[]>): T[] {
  const bundleIds = new Set(
    lines
      .filter((line) => line.lineItemType && BUNDLE_LINE_TYPES.has(line.lineItemType))
      .map((line) => line.id),
  )
  return lines.map((line, index) => {
    const inBundle = line.parentId != null && bundleIds.has(line.parentId)
    const result = applyMeasurements(inBundle ? { ...line, measurements: null } : line)
    if (result.ok) {
      return result.line
    }
    ctx.issues.push({
      code: 'custom',
      message: measurementsErrorMessages[result.reason],
      input: line.measurements,
      path:
        result.reason === 'invalid_formula'
          ? [index, 'measurements', result.rowIndex]
          : [index, 'measurements'],
    })
    return line
  })
}

/**
 * Zod transform for one item that is not a document line (a job material): rejects a bad Aufmaß,
 * or sets the item's quantity from it.
 */
export function enforceItemMeasurements<T extends Parameters<typeof applyMeasurements>[0]>(
  item: T,
  ctx: z.core.$RefinementCtx<T>,
): T {
  const result = applyMeasurements(item)
  if (result.ok) {
    return result.line
  }
  ctx.issues.push({
    code: 'custom',
    message: measurementsErrorMessages[result.reason],
    input: item.measurements,
    path:
      result.reason === 'invalid_formula' ? ['measurements', result.rowIndex] : ['measurements'],
  })
  return item
}

/**
 * For a copy path that changes a line's quantity (partial invoice, storno, final invoice remainder):
 * the Aufmaß only travels with the line when the quantity it adds up to is unchanged. Otherwise
 * the locked quantity would contradict its own Aufmaß.
 */
export function measurementsForCopiedQuantity(
  source: { quantity: number; measurements?: Measurement[] | null },
  quantity: number,
): Measurement[] | null {
  return source.quantity === quantity ? (source.measurements ?? null) : null
}
